/**
 * Transform Supabase data to match component expected format
 */
import defaultPfp from '../assets/default_pfp.png'

// Default category images (map icon names to Unsplash images)
const CATEGORY_IMAGES = {
  facials: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600&h=400&fit=crop',
  haircolor: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&h=400&fit=crop',
  massage: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&h=400&fit=crop',
  footmassage: 'https://images.unsplash.com/photo-1519415510236-718bdfcd89c8?w=600&auto=format&fit=crop&q=60',
  hairstyling: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=600&h=400&fit=crop',
  manicure: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?w=600&h=400&fit=crop',
  waxing: 'https://images.unsplash.com/photo-1552693673-1bf958298935?w=600&h=400&fit=crop',
  pedicure: 'https://images.unsplash.com/photo-1604902396830-aca29e19b067?w=600&h=400&fit=crop',
  default: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600&h=400&fit=crop'
}

// Category descriptions
const CATEGORY_DESCRIPTIONS = {
  'Facials': 'Rejuvenate your skin with our premium facial treatments',
  'Hair Color': 'Transform your look with expert hair coloring',
  'Massage': 'Relax and unwind with therapeutic massage',
  'Foot Massage': 'Pamper your feet with specialized treatments',
  'Hair Styling': 'Professional cuts and styling services',
  'Manicure': 'Beautiful and healthy nails with expert care',
  'Waxing': 'Smooth, hair-free skin with professional waxing',
  'Pedicure': 'Complete foot care and beautification'
}

/**
 * Get category image by icon name or name
 */
const getCategoryImage = (icon, name) => {
  if (icon) {
    const key = icon.toLowerCase().replace(/[^a-z]/g, '')
    return CATEGORY_IMAGES[key] || CATEGORY_IMAGES.default
  }
  if (name) {
    const key = name.toLowerCase().replace(/[^a-z]/g, '')
    return CATEGORY_IMAGES[key] || CATEGORY_IMAGES.default
  }
  return CATEGORY_IMAGES.default
}

/**
 * Get category description
 */
const getCategoryDescription = (name) => {
  return CATEGORY_DESCRIPTIONS[name] || `Explore our ${name} services`
}

/**
 * Transform service from Supabase to component format
 */
export const transformService = (service) => {
  if (!service) return null
  
  return {
    id: service.id,
    name: service.name,
    price: parseFloat(service.price_from),
    duration: `${service.duration_minutes} min`,
    duration_minutes: typeof service.duration_minutes === 'number' ? service.duration_minutes : Number(service.duration_minutes) || undefined,
    description: service.description || '',
    category_id: service.category_id,
    is_active: service.is_active,
    base_currency: service.base_currency || 'USD'
  }
}

/**
 * Transform category with its services
 */
export const transformCategory = (category, services = []) => {
  if (!category) return null

  // Filter services for this category
  const categoryServices = services
    .filter(s => s.category_id === category.id)
    .map(transformService)
    .filter(Boolean)

  return {
    id: category.id,
    name: category.name,
    description: getCategoryDescription(category.name),
    image: getCategoryImage(category.icon, category.name),
    count: categoryServices.length,
    services: categoryServices,
    icon: category.icon,
    sort_order: category.sort_order
  }
}

/**
 * Transform all categories with services
 */
export const transformCategoriesWithServices = (categories, services) => {
  if (!categories || !Array.isArray(categories)) return []
  
  return categories
    .map(cat => transformCategory(cat, services))
    .filter(Boolean)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
}

/**
 * Group services by category
 */
export const groupServicesByCategory = (services) => {
  if (!services || !Array.isArray(services)) return {}
  
  return services.reduce((acc, service) => {
    const catId = service.category_id
    if (!acc[catId]) {
      acc[catId] = []
    }
    acc[catId].push(transformService(service))
    return acc
  }, {})
}

// Default professional placeholder image
const DEFAULT_PROFESSIONAL_IMAGE = defaultPfp

/**
 * Transform professional from Supabase to component format
 */
export const transformProfessional = (professional) => {
  if (!professional) return null
  
  // Extract specializations (preserve full specialization data including excluded_service_ids)
  const specializations = professional.professional_specializations
    ?.map(spec => spec.service_categories?.name)
    .filter(Boolean) || []
  
  // Preserve full professional_specializations for exclusion checking
  const professional_specializations = professional.professional_specializations || []
  
  // Rating is already review-based from getProfessionals (0 when no verified reviews)
  let rating = 0
  if (professional.rating !== null && professional.rating !== undefined && professional.rating !== '') {
    const parsed = parseFloat(professional.rating)
    if (!isNaN(parsed) && isFinite(parsed)) {
      rating = parsed
    }
  }
  
  // Log for debugging (only log if rating exists)
  if (professional.name && rating > 0) {
    console.log(`📊 Professional ${professional.name}: DB rating = ${professional.rating}, Parsed = ${rating}`)
  }
  
  return {
    id: professional.id,
    name: professional.name,
    role: professional.role,
    rating: rating, // Stored DB rating, or live average when verified reviews exist
    reviewCount: typeof professional.reviewCount === 'number' ? professional.reviewCount : 0,
    image: professional.image_url || DEFAULT_PROFESSIONAL_IMAGE, // Components will optimize this
    image_url: professional.image_url, // Keep original URL for optimization in components
    bio: professional.bio || '',
    specializations: specializations,
    professional_specializations: professional_specializations, // Preserve for exclusion checking
    is_active: professional.is_active,
    created_at: professional.created_at,
    updated_at: professional.updated_at
  }
}

/**
 * Transform all professionals
 */
export const transformProfessionals = (professionals) => {
  if (!professionals || !Array.isArray(professionals)) return []
  
  return professionals
    .map(transformProfessional)
    .filter(Boolean)
}

/**
 * Transform salon from Supabase to component format
 */
export const transformSalon = (salon) => {
  if (!salon) return null
  
  return {
    id: salon.id,
    name: salon.name || 'Timeless Trends Salon',
    description: salon.description || '',
    businessHours: salon.business_hours || {},
    additionalInfo: salon.additional_info || {},
    location: salon.location || '',
    city: salon.city || '',
    email: salon.email || '',
    phone: salon.phone || '',
    countryCode: salon.country_code || ''
  }
}

// Default avatar for reviewers without image
const DEFAULT_REVIEWER_AVATAR = 'https://ui-avatars.com/api/?name='

/**
 * Transform review from Supabase to component format
 */
export const transformReview = (review) => {
  if (!review) return null
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently'
    const date = new Date(dateString)
    const now = new Date()
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    
    if (diffInDays === 0) return 'Today'
    if (diffInDays === 1) return 'Yesterday'
    if (diffInDays < 7) return `${diffInDays} days ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`
    return `${Math.floor(diffInDays / 365)} years ago`
  }

  // Use reviewer's name as the main name (person who wrote the review)
  const professionalName = review.professionals?.name || 'Professional'
  const professionalImage = review.professionals?.image_url || null
  const reviewerName = review.reviewer_name || 'Anonymous'
  
  // Use reviewer's avatar, otherwise default avatar with reviewer name
  const avatarUrl = review.reviewer_avatar || `${DEFAULT_REVIEWER_AVATAR}${encodeURIComponent(reviewerName)}`

  return {
    id: review.id,
    name: reviewerName, // Show reviewer's name (person who wrote the review)
    rating: review.rating,
    date: formatDate(review.created_at),
    comment: review.review_text || '',
    service: review.professionals?.role || 'Service',
    image: avatarUrl,
    reviewerName: reviewerName, // Reviewer name (same as name for consistency)
    professionalName: professionalName, // Professional being reviewed
    professionalImage: professionalImage, // Professional image for reference
    isVerified: review.is_verified || false,
    createdAt: review.created_at
  }
}

/**
 * Transform all reviews
 */
export const transformReviews = (reviews) => {
  if (!reviews || !Array.isArray(reviews)) return []
  
  return reviews
    .map(transformReview)
    .filter(Boolean)
}
